import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';

import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { CountryPicker } from '../../../components/country-picker/country-picker';
import { AuthService } from '../../../services/auth/auth.service';
import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';

export interface AvaliacaoFisicaResponse {
  peso: number;
  sexo: string;
  altura: number;
  circunferenciaAbdominal: number;
  etnia: string;
  observacoes?: string;
  nascimento: string;
}

export interface AtualizarAvaliacaoFisicaRequest {
  idPessoa: number;
  peso: number;
  altura: number;
  circunferenciaAbdominal: number;
  etnia: string;
  observacoes: string;
}

@Component({
  selector: 'app-avaliacao-fisica',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatTableModule,
    CountryPicker,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './avaliacao-fisica.html',
  styleUrl: './avaliacao-fisica.scss',
})
export class AvaliacaoFisica implements OnInit {
  constructor(
    private pessoasService: PessoasService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
  ) {}

  dataNascimento?: string;
  peso?: string;
  altura?: string;
  circunferenciaAbdominal?: string;
  etnia?: string;
  outrasInformacoes?: string;
  sexo?: string;

  erroNascimento = signal('');
  erroPeso = signal('');
  erroAltura = signal('');
  erroCircunferenciaAbdominal = signal('');
  erroEtnia = signal('');
  erroOutrasInformacoes = signal('');

  etnias: string[] = ['Caucasiana', 'Latina', 'Africana', 'Asiática'];

  salvando = false;
  pessoaId!: number;
  dadosIniciaisPessoa: AvaliacaoFisicaResponse | null = null;

  async ngOnInit(): Promise<void> {
    await this.buscarDadosAvaliacaoFisica();
  }

  formBloqueado(): boolean {
    const perfisQuePodemEditar = ['ROLE_MASTER', 'ROLE_SAUDE'];
    const perfil = this.authService.getPerfil();
    return !perfisQuePodemEditar.includes(perfil);
  }

  get idade(): number | '' {
    if (!this.dataNascimento) return '';

    const nascimento = new Date(this.dataNascimento);
    const hoje = new Date();

    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();

    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }

    return idade;
  }

  get imcNum(): number {
    const peso = this.pesoEmKg;
    const altura = this.alturaEmMetros;

    if (!peso || !altura) return 0;

    const bloqueiaCalculo = this.altura!.length < 3;
    if (bloqueiaCalculo) return 0;

    return peso / (altura * altura);
  }

  get alturaEmMetros(): number {
    const alturaCm = this.toNumber(this.altura);
    return alturaCm ? alturaCm / 100 : 0;
  }

  get circunferenciaEmCM(): number {
    const ca = Number(this.circunferenciaAbdominal);
    return ca;
  }

  get pesoEmKg() {
    const peso = Number(this.peso);
    return peso;
  }

  get imc(): string {
    return this.imcNum ? this.imcNum.toFixed(2).replaceAll('.', ',') : '';
  }

  get classificacaoImc(): string {
    const imc = this.imcNum;
    if (!imc) return '';

    if (imc < 18.5) return 'Abaixo do peso';
    if (imc < 25) return 'Eutrófico';
    if (imc < 30) return 'Sobrepeso';
    if (imc < 35) return 'Obesidade grau I';
    if (imc < 40) return 'Obesidade grau II';
    return 'Obesidade grau III';
  }

  get riscoComorbidades(): string {
    const imc = this.imcNum;
    if (!imc) return '';

    if (imc < 18.5) return 'Baixo';
    if (imc < 25) return 'Médio';
    if (imc < 30) return 'Pouco elevado';
    if (imc < 35) return 'Elevado';
    if (imc < 40) return 'Muito elevado';
    return 'Muitíssimo elevado';
  }

  get riscoCardiovascular(): string {
    if (!this.circunferenciaAbdominal || !this.etnia || !this.sexo) return '';

    const ca = Number(this.circunferenciaAbdominal);
    if (!ca || !this.etnia || !this.sexo) return '';
    const f = 'Feminino';
    const m = 'Masculino';

    if (this.etnia === 'Caucasiana') {
      if (this.sexo === f) {
        if (ca >= 88) return 'Significativamente elevado';
        if (ca >= 80) return 'Elevado';
      }
      if (this.sexo === m) {
        if (ca >= 102) return 'Significativamente elevado';
        if (ca >= 94) return 'Elevado';
      }
    }

    if (this.etnia === 'Latina') {
      if (this.sexo === f) {
        if (ca >= 90) return 'Significativamente elevado';
        if (ca >= 83) return 'Elevado';
      }
      if (this.sexo === m) {
        if (ca >= 94) return 'Significativamente elevado';
        if (ca >= 88) return 'Elevado';
      }
    }

    if (this.etnia === 'Africana') {
      if (this.sexo === f) {
        if (ca >= 81.5) return 'Significativamente elevado';
        if (ca >= 71.5) return 'Elevado';
      }
      if (this.sexo === m) {
        if (ca >= 80.5) return 'Significativamente elevado';
        if (ca >= 76.5) return 'Elevado';
      }
    }

    if (this.etnia === 'Asiática') {
      if (this.sexo === f && ca >= 80) return 'Elevado';
      if (this.sexo === m && ca >= 85) return 'Elevado';
    }

    return 'Baixo';
  }

  private converterDataParaISO(data: string): string {
    const [dia, mes, ano] = data.split('/');
    return `${ano}-${mes}-${dia}`;
  }

  private converteDadosResponse(
    data: AvaliacaoFisicaResponse,
  ): AvaliacaoFisicaResponse {
    return {
      peso: Number(data.peso?.toFixed(2)) || 0.0,
      sexo: data.sexo ?? '',
      altura: data.altura ? data.altura / 100 : 0.0,
      circunferenciaAbdominal: data.circunferenciaAbdominal ?? 0.0,
      etnia: data.etnia ?? '',
      observacoes: data.observacoes ?? '',
      nascimento: this.converterDataParaISO(data.nascimento) ?? '',
    };
  }

  private atualizaDadosTela(data: AvaliacaoFisicaResponse): void {
    this.peso = data.peso.toFixed(2).replace('.', ',');

    // altura vem em metros → converte para cm e formata com 2 casas
    this.altura = (data.altura * 100).toFixed(2).replace('.', ',');

    // circunferência com 1 casa decimal
    this.circunferenciaAbdominal = data.circunferenciaAbdominal
      .toFixed(1)
      .replace('.', ',');

    this.etnia = data.etnia;
    this.outrasInformacoes = data.observacoes ?? '';
    this.dataNascimento = data.nascimento;
    this.sexo = data.sexo;
  }

  async buscarDadosAvaliacaoFisica(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;

    this.pessoaId = +id;

    try {
      const res = await this.pessoasService.buscarDadosAvaliacaoFisica(
        this.pessoaId,
      );
      if (!res?.data) return;

      const dadosConvertidos = this.converteDadosResponse(res.data);
      this.dadosIniciaisPessoa = dadosConvertidos;
      this.atualizaDadosTela(dadosConvertidos);
    } catch (error) {
      console.error('Erro ao buscar avaliação física', error);
    }
  }

  private validarCamposObrigatorios(): boolean {
    let valido = true;

    if (!this.peso) {
      this.erroPeso.set('Peso é obrigatório');
      valido = false;
    }

    const bloqueiaSalvamentoAltura = this.altura!.length < 3;
    if (!this.altura || bloqueiaSalvamentoAltura) {
      this.erroAltura.set('Altura é obrigatória');
      valido = false;
    }

    if (!this.circunferenciaAbdominal) {
      this.erroCircunferenciaAbdominal.set(
        'Circunferência abdominal é obrigatória',
      );
      valido = false;
    }

    if (!this.etnia) {
      this.erroEtnia.set('Etnia é obrigatória');
      valido = false;
    }

    if (this.outrasInformacoes) {
      if (this.outrasInformacoes.length < 15) {
        this.erroOutrasInformacoes.set(
          'O campo deve ter no mínimo 15 caracteres',
        );
        valido = false;
      }

      if (this.outrasInformacoes.length > 300) {
        this.erroOutrasInformacoes.set(
          'O campo deve ter no máximo 300 caracteres',
        );
        valido = false;
      }
    }

    return valido;
  }

  cancelar(): void {
    if (this.dadosIniciaisPessoa) {
      this.atualizaDadosTela(this.dadosIniciaisPessoa);
    }
  }

  resetErros(): void {
    this.erroAltura.set('');
    this.erroCircunferenciaAbdominal.set('');
    this.erroEtnia.set('');
    this.erroNascimento.set('');
    this.erroOutrasInformacoes.set('');
    this.erroPeso.set('');
  }

  async salvarAvaliacaoFisica(): Promise<void> {
    this.resetErros();

    if (!this.validarCamposObrigatorios()) return;

    this.salvando = true;

    try {
      await this.pessoasService.salvaDadosAvaliacaoFisica({
        idPessoa: this.pessoaId,
        peso: this.pesoEmKg,
        altura: this.alturaEmMetros,
        circunferenciaAbdominal: this.circunferenciaEmCM,
        etnia: this.etnia!,
        observacoes: this.outrasInformacoes ?? '',
      });

      this.snackbarService.success('Dados salvos com sucesso');
    } finally {
      this.salvando = false;
    }
  }

  private toNumber(valor?: string): number {
    if (!valor) return 0;

    return Number(
      valor
        .toString()
        .replace(/\./g, '') // remove separador de milhar
        .replace(',', '.'), // troca vírgula por ponto
    );
  }
}
